import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import CreateUserForm from './CreateUserForm';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  suspended: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

const ROLE_LABELS = {
  admin: 'Administrador',
  customer: 'Cliente',
};

const UserTable = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 11;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await api.get('/api/users');
        const sorted = res.data.sort((a, b) => (a.user_id || '').localeCompare(b.user_id || ''));
        setUsuarios(sorted);
      } catch (err) {
        console.error('Error al cargar usuarios:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsuarios();
  }, []);

  const filtered = usuarios.filter((u) =>
    (!filterStatus || u.status === filterStatus) &&
    (u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
     u.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const paginatedUsers = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleStatusChange = (e) => setFilterStatus(e.target.value);

  const handleCreateUserSuccess = () => {
    setShowCreateModal(false);
    api.get('/api/users').then((res) => setUsuarios(res.data));
  };

  return (
    <div className="bg-[oklch(97%_0.014_254.604)] min-h-screen py-4 px-4 sm:px-6 lg:px-8 font-[Manrope]">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Suscriptores</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                Lista de todos los usuarios registrados incluyendo nombre, correo y estado.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center rounded-md bg-[oklch(54.6%_0.245_262.881)] px-3 py-2 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-[oklch(54.6%_0.245_262.881)]/90"
              >
                + Usuario
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <input
              type="text"
              placeholder="Buscar por nombre o correo"
              value={searchQuery}
              onChange={handleSearchChange}
              className="p-2 border border-gray-300 rounded-md w-full text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filterStatus}
              onChange={handleStatusChange}
              className="p-2 border border-gray-300 rounded-md w-full sm:w-auto text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="suspended">Suspendido</option>
              <option value="pending">Pendiente</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-3 sm:px-4">Nombre</th>
                  <th className="px-2 py-3 sm:px-4">Correo</th>
                  <th className="px-2 py-3 sm:px-4">Estado</th>
                  <th className="px-2 py-3 sm:px-4 hidden sm:table-cell">Rol</th>
                  <th className="px-2 py-3 sm:px-4 hidden md:table-cell">Creado</th>
                  <th className="px-2 py-3 sm:px-4">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-2 py-3 sm:px-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center bg-gray-200 rounded-full text-xs font-bold text-gray-600">
                          {u.first_name?.charAt(0)}{u.last_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-xs sm:text-sm">{u.full_name}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500 font-mono">ID: {u.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 sm:px-4 text-gray-700 whitespace-nowrap">{u.email}</td>
                    <td className="px-2 py-3 sm:px-4">
                      <span className={`inline-block text-xs px-2 py-1 rounded ${STATUS_COLORS[u.status] || 'bg-gray-100 text-gray-800'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-2 py-3 sm:px-4 hidden sm:table-cell text-gray-700">
                      {ROLE_LABELS[u.role] || u.role}
                    </td>
                    <td className="px-2 py-3 sm:px-4 hidden md:table-cell text-gray-600 whitespace-nowrap">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-3 sm:px-4">
                      <button
                        className="text-blue-700 hover:underline text-xs sm:text-sm"
                        onClick={() => navigate(`/usuarios/${u.id}`)}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 text-xs sm:text-sm text-gray-600 gap-4">
            <p>Página {currentPage} de {totalPages}</p>
            <div className="space-x-2">
              <button
                className="px-2 py-1 sm:px-3 sm:py-1 border rounded disabled:opacity-50 text-xs sm:text-sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </button>
              <button
                className="px-2 py-1 sm:px-3 sm:py-1 border rounded disabled:opacity-50 text-xs sm:text-sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Crear Usuario */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center">
          <Dialog.Panel className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md sm:max-w-lg shadow-xl">
            <CreateUserForm onCreate={handleCreateUserSuccess} />
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default UserTable;